import { Capacitor } from '@capacitor/core'
import { LAB_CLOUD_URL } from './constants'
import {
  environmentConfigs,
  type TFirebaseProjectID,
} from '@team-milva/lab-constants/environments'

const emulators = import.meta.env.VITE_APP_EMULATORS
const isEmulatingFunctions = emulators && emulators.includes('functions')
const isEmulatingFirestore = emulators && emulators.includes('firestore')
const isEmulatingAuth = emulators && emulators.includes('auth')
const isEmulatingStorage = emulators && emulators.includes('storage')

// Which environment this BUILD targets, decided by the Vite mode: `production` → prod, `stage` →
// stage, anything else (`development`, `test`) → dev. It is baked in at build time rather than read
// from Hosting at runtime (as lab-management does) because this app also ships as a Capacitor shell,
// which has no Hosting origin to ask. `npm run build:stage` / `build` / `build:dev` pick the mode.
export function resolveProjectID(mode: string): TFirebaseProjectID {
  if (mode === 'production') {
    return 'lab-services-prod'
  }
  return mode === 'stage' ? 'lab-services-stage' : 'lab-services-dev'
}

const projectID = resolveProjectID(import.meta.env.MODE)
const environment = environmentConfigs[projectID]
const isProd = projectID === 'lab-services-prod'
const isStage = projectID === 'lab-services-stage'
// "Not prod" — dev tooling and console diagnostics stay on for stage too, as they always have.
const isDev = !isProd
// Native Capacitor builds also report hostname 'localhost' (the default WebView
// host), so exclude them here — this flag is only meant to detect the Vite dev server.
const isLocalhost =
  !Capacitor.isNativePlatform() &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname)

// Cloud Functions host URL — the emulator, or the environment's own deployed functions.
const functionsURL = isEmulatingFunctions ? LAB_CLOUD_URL.EMULATOR : environment.cloudFunctionsHost

// Cloudinary and the translations CDN come from the same shared per-environment config
// lab-management uses (environmentConfigs), never a hand-typed second copy of these URLs.
// A hand-typed 'dev-lab'/'prod-lab' pair once drifted from the real Cloudinary delivery
// prefixes and 404'd every image (confirmed live 2026-09-16), and a hardcoded prod translations
// host (`lab-translations.web.app`) answers "Site Not Found" — the real one is
// `lab-translations-prod.web.app`.
const cloudinary = environment.cloudinaryHostURL
const translations = environment.translationsCDNHost

export const ENV = Object.freeze({
  isProd,
  isStage,
  isDev,
  isLocalhost,
  projectID,
  environmentName: environment.environmentName,
})

export const HOST = Object.freeze({
  functions: functionsURL,
  cloudinary,
  translations,
})

export const EMU = Object.freeze({
  isEmulatingFunctions,
  isEmulatingFirestore,
  isEmulatingAuth,
  isEmulatingStorage,
})

